import { type GQL } from "@binale-tech/shared";

// "sondervz" (SP2, FIBU-22): the Sondervorauszahlung informational row, §47 UStDV -
// monthly UStVA filers only, only when Dauerfristverlängerung is granted. A wholly new
// category, additive to the SP1 synthetic-id contract (design spec §14.2).
export type EventCategory = "ustva" | "sondervz" | "vz-est" | "vz-kst" | "vz-gewst" | "annual" | "offenlegung" | "user";

export type EventGroup = "heute" | "woche" | "monat" | "spaeter";

// SP1 (FIBU-22) cross-SP contract (design spec §14.2): every merged UI event carries a
// `source` discriminator + a stable `stateKey` - `calendarEvents.id` for user rows,
// the synthetic id (== `id` below) for derived rows. SP3 keys its per-item state table
// off `stateKey` uniformly for both kinds; don't drop these when reshaping this type.
export type CalendarEventSource = "user" | "derived";

export interface CalendarEvent {
    id: string;
    date: Date; // hard/legal due date, weekend-shifted
    category: EventCategory;
    titleId: string; // react-intl id
    titleValues?: Record<string, string | number>;
    amountCents?: number; // UStVA (current period only) and vz-est/vz-kst/vz-gewst (SP2)
    softDate?: Date; // annual returns only: earlier self-preparer date
    link?: string; // AppRoutes deep link; undefined = informational row
    source: CalendarEventSource;
    stateKey: string;
    // Set either at event-creation time (user events, SP1) or overlaid from
    // calendarItemState.color (SP3, both sources - overlayItemState wins once a
    // state row exists, design spec §III.7) - always the DocumentColors palette.
    color?: GQL.IDocumentColors;
    // SP3 (FIBU-22): per-item state overlaid from `calendarItemState`, keyed by
    // `stateKey` (design spec §III.5–§III.7). `done`/`note` are user-facing;
    // `acknowledged` is schema-complete but has no UI in SP3 (§III.11 decision #3).
    done?: boolean;
    acknowledged?: boolean;
    note?: string;
}

export interface LegalFormCategory {
    isKapitalgesellschaft: boolean; // → KSt-VZ (else ESt-VZ)
    isGewerbe: boolean; // → GewSt-VZ + GewSt-Erklärung
    offenlegungspflichtig: boolean; // → Offenlegung
    known: boolean; // false → suppress all VZ/Offenlegung rows
}

export interface TaxDeadlineInput {
    today: Date;
    year: number; // the selected accounting year (GlobalYearSelect)
    legalForm: string | null | undefined;
    taxation: GQL.ICompanyTaxation | null | undefined;
    fiscalYearStart: string | null | undefined;
    bundesland: string | null | undefined; // reserved for holiday-aware shift (unused in v1)
    // SP2 (FIBU-22), all optional/nullable and backward-compatible when absent - the
    // monthly/no-DFV/both-annual-dates path below must stay byte-identical to SP1's
    // output (design spec §II.7/§14):
    ustvaFrequency?: GQL.IUstvaFilingFrequency | null; // undefined/null → MONTHLY (today's behavior)
    dauerfristverlaengerung?: boolean | null; // falsy → off (today's behavior)
    steuerlichBeraten?: boolean | null; // undefined → both annual dates shown (today's behavior)
}
